import React, { useState, useEffect } from 'react';
import { useMutation, gql, useQuery } from '@apollo/client';
import RoomSelection from './RoomSelection';
import TravelerForm from './TravelerForm';
import RoomAssignment from './RoomAssignment';
import type { BusinessOffer } from '../../../types/graphql';

// Définir les types d'entrée corrects
interface RoomInput {
  type: string;
  capacity: number;
}

interface ClientInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  agencyId: number;
}

interface RoomAssignmentInput {
  roomId: number;
  clientIds: number[];
}

const ACCEPT_BUSINESS_OFFER = gql`
  mutation AcceptBusinessOffer(
    $offerId: Int!
    $rooms: [RoomInput!]!
    $clients: [ClientInput!]!
    $assignments: [RoomAssignmentInput!]!
  ) {
    acceptBusinessOffer(
      offerId: $offerId
      rooms: $rooms
      clients: $clients
      assignments: $assignments
    ) {
      id
      status
      validUntil
      createdAt
      updatedAt
    }
  }
`;

const GET_BUSINESS_AGENCY = gql`
  query GetBusinessAgency($businessId: Int!) {
    business(id: $businessId) {
      id
      companyName
      agencyId
      agency {
        id
        name
      }
    }
  }
`;

interface AcceptOfferFormProps {
  offer: BusinessOffer;
  onSuccess: () => void;
  onClose: () => void;
}

// Fonction pour déterminer la capacité d'une chambre en fonction de son type
const getRoomCapacity = (type: string): number => {
  switch (type.toUpperCase()) {
    case 'SINGLE':
      return 1;
    case 'DOUBLE':
      return 2;
    case 'TRIPLE':
      return 3;
    case 'SUITE':
      return 4;
    default:
      return 2; // Valeur par défaut
  }
};

export default function AcceptOfferForm({ offer, onSuccess, onClose }: AcceptOfferFormProps) {
  const [step, setStep] = useState(1);
  const [rooms, setRooms] = useState<any[]>([]);
  const [travelers, setTravelers] = useState<any[]>([{
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    passport: {
      number: '',
      expiryDate: '',
      file: ''
    }
  }]);
  const [assignments, setAssignments] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);

  // Utiliser directement l'ID de l'entreprise à partir de l'offre
  const businessId = offer.business?.id || offer.businessId;

  const { data: businessData, loading: businessLoading, error: businessError } = useQuery(GET_BUSINESS_AGENCY, {
    variables: { businessId },
    skip: !businessId
  });

  const [acceptOffer, { loading }] = useMutation(ACCEPT_BUSINESS_OFFER);

  const handleSubmit = async () => {
    try {
      if (!businessData?.business?.agencyId) {
        throw new Error("L'ID de l'agence est requis pour créer des clients");
      }

      // Vérification manuelle des assignations
      let hasValidAssignment = false;
      for (const [roomId, travelerIds] of Object.entries(assignments)) {
        if (travelerIds && travelerIds.length > 0) {
          hasValidAssignment = true;
          break;
        }
      }

      if (!hasValidAssignment) {
        throw new Error("Aucune assignation valide n'a été trouvée. Veuillez assigner au moins un voyageur à une chambre.");
      }

      // Préparer les données des chambres
      const roomInputs = rooms.map(room => ({
        type: room.type.toUpperCase(),
        capacity: getRoomCapacity(room.type)
      }));

      // Préparer les données des clients
      const clientInputs = travelers.map(traveler => ({
        firstName: traveler.firstName,
        lastName: traveler.lastName,
        email: traveler.email,
        phone: traveler.phone,
        agencyId: businessData.business.agencyId
      }));

      // Créer une correspondance entre les identifiants de chambres et leurs indices
      const roomMap = {};
      let roomCounter = 0;
      
      rooms.forEach((roomType, typeIndex) => {
        for (let i = 0; i < roomType.count; i++) {
          const roomId = `${roomType.type}-${i}`;
          roomMap[roomId] = roomCounter++;
        }
      });
      

      // Convertir les assignations en utilisant les indices
      const processedAssignments: RoomAssignmentInput[] = [];
      
      Object.entries(assignments).forEach(([roomId, travelerIds]) => {
        if (travelerIds && travelerIds.length > 0) {
          const roomIndex = roomMap[roomId] !== undefined ? roomMap[roomId] : parseInt(roomId);
          
          // Filtrer les IDs null ou undefined et convertir en nombres
          const clientIds = travelerIds
            .filter(id => id !== null && id !== undefined)
            .map(id => {
              // Si l'ID est au format "traveler-X", extraire X
              if (typeof id === 'string' && id.startsWith('traveler-')) {
                return parseInt(id.split('-')[1]);
              }
              return parseInt(id);
            })
            .filter(id => !isNaN(id)); // Filtrer les valeurs NaN
          
          // Ne créer l'assignation que si nous avons des IDs valides
          if (clientIds.length > 0) {
            processedAssignments.push({
              roomId: roomIndex,
              clientIds
            });
          }
        }
      });
      
      // Vérifier que nous avons des assignations valides
      if (processedAssignments.length === 0) {
        throw new Error("Aucune assignation valide n'a été trouvée. Veuillez assigner au moins un voyageur à une chambre.");
      }

     

      // Envoyer la mutation
      const { data } = await acceptOffer({
        variables: {
          offerId: offer.id,
          rooms: roomInputs,
          clients: clientInputs,
          assignments: processedAssignments
        }
      });

      // Gérer le succès
      if (data?.acceptBusinessOffer) {
        onSuccess();
      }
    } catch (error) {
      console.error('Erreur lors de l\'acceptation de l\'offre:', error);
      setError(error.message);
    }
  };

  // Afficher un message d'erreur si nous ne pouvons pas récupérer les données de l'agence
  if (businessError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600 font-medium">Erreur lors de la récupération des données de l'agence:</p>
        <p className="text-red-500">{businessError.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {businessLoading && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-600">Chargement des données de l'agence...</p>
        </div>
      )}

      {step === 1 && (
        <RoomSelection
          value={rooms}
          onChange={setRooms}
          maxTravelers={offer.minimumParticipants}
        />
      )}

      {step === 2 && (
        <TravelerForm
          travelers={travelers}
          onChange={setTravelers}
        />
      )}

      {step === 3 && (
        <RoomAssignment
          roomPreferences={rooms}
          travelers={travelers}
          assignments={assignments}
          onAssign={setAssignments}
        />
      )}

      <div className="flex justify-between mt-6">
        {step > 1 && (
          <button
            type="button"
            onClick={() => setStep(step - 1)}
            className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-50"
          >
            Précédent
          </button>
        )}

        {step < 3 ? (
          <button
            type="button"
            onClick={() => setStep(step + 1)}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Suivant
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || businessLoading || !businessData?.business?.agencyId}
            className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'En cours...' : 'Confirmer'}
          </button>
        )}
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 font-medium">Erreur lors de l'acceptation de l'offre:</p>
          <p className="text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
} 